import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'
import { useSubscription } from '../lib/useSubscription'
import SubscribeButton from '../components/SubscribeButton'

export default function TrustDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [trust, setTrust] = useState(null)
  const [myRole, setMyRole] = useState(null)
  const [members, setMembers] = useState([])
  const [accounts, setAccounts] = useState([])
  const [beneficiaries, setBeneficiaries] = useState([])
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)
  const { isActive, isTrialing, subscription } = useSubscription(id)

  useEffect(() => {
    if (id) fetchTrustData()
  }, [id])

  const fetchTrustData = async () => {
    setLoading(true)

    // Fetch trust
    const { data: trustData } = await supabase
      .from('trusts')
      .select('*')
      .eq('id', id)
      .single()
    setTrust(trustData)

    // Fetch my role
    const { data: memberData } = await supabase
      .from('trust_members')
      .select('role')
      .eq('trust_id', id)
      .eq('user_id', user.id)
      .single()
    setMyRole(memberData?.role)

    // Fetch all members
    const { data: allMembers } = await supabase
      .from('trust_members')
      .select('*')
      .eq('trust_id', id)
    setMembers(allMembers || [])

    // Fetch accounts
    const { data: accountData } = await supabase
      .from('accounts')
      .select('*')
      .eq('trust_id', id)
    setAccounts(accountData || [])

    // Fetch beneficiaries
    const { data: benData } = await supabase
      .from('beneficiaries')
      .select('*')
      .eq('trust_id', id)
    setBeneficiaries(benData || [])

    setLoading(false)
  }

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD'
  }).format(amount || 0)

  const canEdit = ['owner', 'admin'].includes(myRole)

  const tabs = ['overview', 'accounts', 'beneficiaries', 'members']

   if (!id) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading...</p>
        </div>
      </Layout>
    )
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading trust...</p>
        </div>
      </Layout>
    )
  }

  if (!trust) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-400">Trust not found.</p>
          <button onClick={() => navigate('/trusts')} className="text-blue-600 text-sm mt-2">
            Back to trusts
          </button>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/trusts')}
              className="text-gray-400 hover:text-gray-600 text-sm"
            >
              ← Back
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{trust.name}</h2>
              <p className="text-sm text-gray-500">
                {trust.state_of_domicile} · Established {trust.date_established
                  ? new Date(trust.date_established).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                  : 'Unknown'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
              trust.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {trust.status}
            </span>
            {canEdit && (
              <button
                onClick={() => navigate(`/trusts/${id}/edit`)}
                className="text-sm border border-gray-300 text-gray-600 hover:bg-gray-50 px-3 py-1.5 rounded-lg transition"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Subscription Banner */}
        {!isActive && !isTrialing && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-amber-900">Trial Expired</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Your 14-day free trial for this trust has ended.
                  Subscribe to continue adding transactions and generating reports.
                </p>
              </div>
              {id && <SubscribeButton trustId={id} trustName={trust?.name} />}
            </div>
          </div>
        )}

      {isTrialing && (
  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h3 className="font-semibold text-blue-900">Free Trial Active</h3>
        <p className="text-sm text-blue-700 mt-1">
          🎉 You are in your 14-day free trial for this trust.
          Subscribe now to keep full access after your trial ends.
        </p>
      </div>
      {console.log('Passing to SubscribeButton - id:', id, 'trust name:', trust?.name)}
      {id && <SubscribeButton trustId={id} trustName={trust?.name} />}
    </div>
  </div>
)}

        {isActive && subscription && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm text-green-700">
              ✓ Active subscription — renews{' '}
              {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric'
              })}
            </p>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Accounts</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{accounts.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Beneficiaries</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{beneficiaries.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Total Principal</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {formatCurrency(accounts.reduce((s, a) => s + (a.principal_balance || 0), 0))}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Total Income</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {formatCurrency(accounts.reduce((s, a) => s + (a.income_balance || 0), 0))}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 text-sm font-medium capitalize transition-colors ${
                  activeTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <h3 className="font-semibold text-gray-800">Trust Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">State of Domicile</p>
                <p className="text-sm font-medium text-gray-800 mt-1">{trust.state_of_domicile || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Jurisdiction</p>
                <p className="text-sm font-medium text-gray-800 mt-1">{trust.jurisdiction || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Date Established</p>
                <p className="text-sm font-medium text-gray-800 mt-1">
                  {trust.date_established
                    ? new Date(trust.date_established).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Status</p>
                <p className="text-sm font-medium text-gray-800 mt-1 capitalize">{trust.status}</p>
              </div>
            </div>
            {trust.principal_income_allocation_rules && (
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                  Principal & Income Allocation Rules
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {trust.principal_income_allocation_rules}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'accounts' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Accounts</h3>
              {canEdit && (
                <button
                  onClick={() => navigate(`/trusts/${id}/accounts/new`)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                >
                  + Add Account
                </button>
              )}
            </div>
            {accounts.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-gray-400 text-sm">No accounts yet</p>
                {canEdit && (
                  <button
                    onClick={() => navigate(`/trusts/${id}/accounts/new`)}
                    className="mt-3 text-blue-600 text-sm hover:underline"
                  >
                    Add first account
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {accounts.map(account => (
                  <div key={account.id} className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-medium text-gray-900">{account.name}</p>
                        <p className="text-xs text-gray-400 capitalize">
                          {account.account_type} {account.asset_subtype ? `· ${account.asset_subtype}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100">
                      <div>
                        <p className="text-xs text-gray-400">Principal</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency(account.principal_balance)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Income</p>
                        <p className="text-sm font-semibold text-green-600">
                          {formatCurrency(account.income_balance)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'beneficiaries' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Beneficiaries</h3>
              {canEdit && (
                <button
                  onClick={() => navigate(`/trusts/${id}/beneficiaries/new`)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                >
                  + Add Beneficiary
                </button>
              )}
            </div>
            {beneficiaries.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-gray-400 text-sm">No beneficiaries yet</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Interest</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {beneficiaries.map(ben => (
                      <tr key={ben.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{ben.full_name}</td>
                        <td className="px-4 py-3 capitalize text-gray-600">{ben.beneficiary_type?.replace('_', ' ')}</td>
                        <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{ben.interest_description || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            ben.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {ben.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'members' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Trust Members</h3>
              {canEdit && (
                <button
                  onClick={() => navigate(`/trusts/${id}/invite`)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                >
                  + Invite Member
                </button>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Credentials</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {members.map(member => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {member.profiles?.full_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{member.profiles?.email || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-700 capitalize">
                          {member.role?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{member.profiles?.credentials || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </Layout>
  )
}